// Budget lifecycle — pure FSM (spec §7).
//
//   Draft → Validated → Published → Tracking → Reforecast(n)
//
// Same shape as the tail-spend deal FSM: a single pure `transition` returning
// either the next state or a reason it's invalid. The reducer (engine.ts) is
// the only caller.

export type BudgetState =
    | { kind: "Draft" }
    | { kind: "Validated" }
    | { kind: "Published" }
    | { kind: "Tracking" }
    | { kind: "Reforecast"; n: number };

export type BudgetStateKind = BudgetState["kind"];

export type BudgetEvent =
    | { type: "SubmitForValidation" }
    | { type: "Validate" }
    | { type: "Publish" }
    | { type: "BeginTracking" }
    | { type: "Reforecast" };

export type Transition =
    | { ok: true; next: BudgetState }
    | { ok: false; reason: string };

function assertNever(x: never): never {
    throw new Error(`Unhandled case: ${JSON.stringify(x)}`);
}

export function transition(state: BudgetState, event: BudgetEvent): Transition {
    const invalid = (): Transition => ({
        ok: false,
        reason: `Event "${event.type}" is not valid in state "${state.kind}"`,
    });

    switch (state.kind) {
        case "Draft":
            // SubmitForValidation and Validate both advance a draft; the demo
            // treats validation as a single approve step.
            return event.type === "SubmitForValidation" || event.type === "Validate"
                ? { ok: true, next: { kind: "Validated" } }
                : invalid();
        case "Validated":
            return event.type === "Publish"
                ? { ok: true, next: { kind: "Published" } }
                : invalid();
        case "Published":
            return event.type === "BeginTracking"
                ? { ok: true, next: { kind: "Tracking" } }
                : invalid();
        case "Tracking":
            return event.type === "Reforecast"
                ? { ok: true, next: { kind: "Reforecast", n: 1 } }
                : invalid();
        case "Reforecast":
            return event.type === "Reforecast"
                ? { ok: true, next: { kind: "Reforecast", n: state.n + 1 } }
                : invalid();
        default:
            return assertNever(state);
    }
}

export function stateLabel(state: BudgetState): string {
    switch (state.kind) {
        case "Draft":
            return "Draft";
        case "Validated":
            return "Validated";
        case "Published":
            return "Published";
        case "Tracking":
            return "Tracking";
        case "Reforecast":
            return `Reforecast ${state.n}`;
        default:
            return assertNever(state);
    }
}

export function stateTone(
    state: BudgetState
): "slate" | "blue" | "teal" | "green" {
    switch (state.kind) {
        case "Draft":
            return "slate";
        case "Validated":
            return "blue";
        case "Published":
            return "teal";
        case "Tracking":
        case "Reforecast":
            return "green";
        default:
            return assertNever(state);
    }
}

/** The next forward event for a budget, used to label a single "advance"
 * button on the forecast screen. Null once tracking. */
export function nextLifecycleEvent(
    state: BudgetState
): { event: BudgetEvent; label: string } | null {
    switch (state.kind) {
        case "Draft":
            return { event: { type: "Validate" }, label: "Validate budget" };
        case "Validated":
            return { event: { type: "Publish" }, label: "Publish budget" };
        case "Published":
            return { event: { type: "BeginTracking" }, label: "Begin tracking" };
        case "Tracking":
        case "Reforecast":
            return null;
        default:
            return assertNever(state);
    }
}
